import { Link } from "react-router-dom";
import { Bitcoin, LogOut, Settings, Shield } from "lucide-react";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import { usePrice } from "@/hooks/usePrice";
import { fmtUsd, fmtBtc } from "@/lib/api";

export const Ticker = () => {
  const p = usePrice();
  const up = (p?.change_24h ?? 0) >= 0;
  return (
    <div className="flex items-center gap-2 rounded-full border border-[#212638] bg-[#0F111A] px-3 py-1.5" data-testid="live-btc-price">
      <span className="h-2 w-2 rounded-full bg-emerald-500 pulse-dot" />
      <span className="text-xs text-[#94A3B8]">BTC/USD</span>
      <span className="font-mono text-sm font-semibold">{p ? fmtUsd(p.price) : "—"}</span>
      {p && <span className={`font-mono text-xs ${up ? "text-emerald-400" : "text-red-400"}`}>{up ? "+" : ""}{p.change_24h.toFixed(2)}%</span>}
    </div>
  );
};

export const Logo = () => (
  <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
    <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#F7931A] text-[#08090E]"><Bitcoin size={20} strokeWidth={2.5} /></span>
    <span className="font-display text-xl font-bold tracking-tight">Bitwallet<span className="text-[#F7931A]">.live</span></span>
  </Link>
);

export const Header = () => {
  const { user, login, logout } = useAuth();
  return (
    <header className="fixed inset-x-0 top-0 z-50 h-16 border-b border-[#212638] bg-[#08090E]/80 backdrop-blur-xl sm:h-20">
      <div className="mx-auto flex h-full max-w-7xl items-center justify-between gap-4 px-4 md:px-8">
        <div className="flex items-center gap-6"><Logo /><div className="hidden md:block"><Ticker /></div></div>
        {user ? (
          <div className="flex items-center gap-3" data-testid="user-profile-menu">
            <div className="hidden items-center gap-2 lg:flex">
              <span className="rounded-full bg-[#151824] px-3 py-1.5 font-mono text-xs" data-testid="header-usd-balance">{fmtUsd(user.usd_balance)}</span>
              <span className="rounded-full bg-[#151824] px-3 py-1.5 font-mono text-xs text-[#F7931A]" data-testid="header-btc-balance">{fmtBtc(user.btc_balance)} BTC</span>
            </div>
            <Link to="/dashboard" data-testid="nav-dashboard-link" className="hidden text-sm text-[#94A3B8] transition-colors hover:text-white sm:inline">Dashboard</Link>
            {user.is_admin && (
              <Link to="/admin" data-testid="nav-admin-link" className="flex items-center gap-1 text-sm text-[#94A3B8] transition-colors hover:text-[#F7931A]"><Shield size={14} /> <span className="hidden sm:inline">Admin</span></Link>
            )}
            <NotificationBell />
            <Link to="/settings" title="Settings" data-testid="nav-settings-link" className="rounded-full border border-[#212638] p-2 text-[#94A3B8] transition-colors hover:bg-[#1C2030] hover:text-white"><Settings size={16} /></Link>
            {user.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-[#212638]" />}
            <button onClick={logout} data-testid="logout-button" className="flex items-center gap-1.5 rounded-full border border-[#212638] px-3 py-1.5 text-sm text-[#94A3B8] transition-colors hover:bg-[#1C2030] hover:text-white">
              <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        ) : (
          <button onClick={login} data-testid="login-button" className="rounded-full bg-[#F7931A] px-5 py-2 text-sm font-semibold text-[#08090E] transition-transform hover:scale-[1.03] hover:bg-amber-500">
            Sign in with Google
          </button>
        )}
      </div>
    </header>
  );
};
